import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import requestClient from '../../../utils/requestClient';
import FormInput from '../../../components/Elements/Form/FormInput';
import ErrorMessage from '../../../components/Elements/ErrorMessage';

const UserCreate = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const locationRef = useRef(null);
  const departmentRef = useRef(null);
  const userTypeRef = useRef(null);

  // Fetch locations and departments when component mounts
  useEffect(() => {
    async function fetchCompanyData() {
      try {
        const response = await requestClient().get('/api/v2/employer/company/');
        if (response.data?.data) {
          setLocations(response.data.data.locations || []);
          setDepartments(response.data.data.departments || []);
        }
      } catch (error) {
        console.error('Failed to fetch company data:', error);
      }
    }
    fetchCompanyData();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors(null);
    setIsSaving(true);

    try {
      const formData = {
        first_name: firstNameRef.current.value,
        last_name: lastNameRef.current.value,
        email: emailRef.current.value,
        location: locationRef.current.value ? parseInt(locationRef.current.value) : null,
        department: departmentRef.current.value ? parseInt(departmentRef.current.value) : null,
        user_type: userTypeRef.current.value || null
      };

      await requestClient().post('/api/v2/employer/employees/', formData);
      navigate('/employer/users', {
        state: {
          successMessage: 'User created successfully'
        }
      });
    } catch (error) {
      setErrors(error.response?.data?.error || ['Failed to create user']);
      setIsSaving(false);
    }
  };

  return (
    <main>
      <section className="bg-white">
        <div className='container py-8 lg:py-12'>
          <div className='border-b pb-8 flex lg:items-center lg:justify-between mb-8 gap-4 flex-col lg:flex-row'>
            <div>
              <nav className='hidden lg:block mb-4'>
                <ul className='text-sm uppercase flex items-center'>
                  <li>
                    <Link to="/employer/users">Users</Link>
                    <span className='mx-2'>/</span>
                  </li>
                  <li className='text-purple font-bold'>
                    Create
                  </li>
                </ul>
              </nav>
              <h1>Create user</h1>
            </div>
            <Link to="/employer/users" className="block lg:hidden">← USERS</Link>
          </div>

          {errors ? <ErrorMessage title="Something is not right" errors={errors} className="mb-4" /> : null}

          <div className='max-w-[600px]'>
            <form onSubmit={handleSubmit} className='grid gap-4 mb-6'>
              <FormInput
                label="Email*"
                type="email"
                name="email"
                inputRef={emailRef}
                props={{ 'required': 'required' }}
              />

              <FormInput
                label="First Name*"
                type="text"
                name="first_name"
                inputRef={firstNameRef}
                props={{ 'required': 'required' }}
              />

              <FormInput
                label="Last Name*"
                type="text"
                name="last_name"
                inputRef={lastNameRef}
                props={{ 'required': 'required' }}
              />

              <div>
                <label className='block text-sm font-bold mb-2'>User Type</label>
                <select
                  ref={userTypeRef}
                  name="user_type"
                  className='w-full px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-purple'
                >
                  <option value="">Select a user type</option>
                  <option value="employee">Employee</option>
                  <option value="employer">Employer</option>
                </select>
              </div>

              <div>
                <label className='block text-sm font-bold mb-2'>Location</label>
                <select
                  ref={locationRef}
                  name="location"
                  className='w-full px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-purple'
                >
                  <option value="">Select a location</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className='block text-sm font-bold mb-2'>Department</label>
                <select
                  ref={departmentRef}
                  name="department"
                  className='w-full px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-purple'
                >
                  <option value="">Select a department</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='flex gap-4 mt-6'>
                <button
                  type="submit"
                  className='btn btn-purple'
                  disabled={isSaving}
                >
                  {isSaving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default UserCreate;